import { Injectable } from '@angular/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { Subject } from 'rxjs';

import {
  MenuConfigItem,
  MenuConfigItemFeatureFlagsEntry,
  MenuConfigItemFeatureFlagHash,
  MenuConfigItemFeatureFlags,
} from '@app/core/_config/channics-sidebar/menu.config';
import { UserPermissionCodeConstant } from '@app/core/_constant/index.constants';
import { AuthService } from '@app/core/auth';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';

@Injectable()
export class MenuConfigService {
  onConfigUpdated$ :Subject<any>;

  private menuConfig :{ header :{ items :[] }; aside :{ items :[] } };

  constructor(
    private authService :AuthService,
    private ft :FeatureToggleService,
    private permissionsService :NgxPermissionsService,
  ) {
    // register on config changed event and set default config
    this.onConfigUpdated$ = new Subject();
  }

  getMenus() {
    return this.menuConfig;
  }

  checkPermissions(
    items :MenuConfigItem[],
    usersPermissions :RPermission,
    displayPageSettings :any = {},
  ) {
    const isViewOnlyUser =
      this.authService.loginUser.prwx < UserPermissionCodeConstant.ReadWriteExecute;

    return items.reduce((acc, next) => {
      if (next.submenu) {
        next.submenu = this.checkPermissions(
          next.submenu,
          usersPermissions,
          displayPageSettings,
        );
      }

      const isHiddenByDefault = next.isHiddenByDefault;
      let isHiddenByPageSettingState = false;
      let isHiddenWithoutSettingState =
        !displayPageSettings && isHiddenByDefault !== undefined;
      if (displayPageSettings) {
        const displayPageSettingsCount = Object.keys(displayPageSettings).length;
        const displayPageSetting = displayPageSettings[next.page];

        isHiddenByPageSettingState =
          displayPageSettingsCount > 0 &&
          displayPageSetting !== undefined &&
          displayPageSetting === false;
        isHiddenWithoutSettingState =
          isHiddenByDefault === true &&
          ((displayPageSettingsCount <= 0 && isHiddenByDefault !== undefined) ||
            (displayPageSettingsCount > 0 && displayPageSetting === undefined));
      }

      if (isHiddenByPageSettingState || isHiddenWithoutSettingState) {
        return acc;
      }

      if (Array.isArray(next?.featureFlags) && next?.featureFlags?.length) {
        // Check all feature flag and all must be true
        if (
          !next.featureFlags.every((flag) => {
            return this.ft.featureEnabled(flag);
          })
        ) {
          return acc;
        }
      } else if (
        typeof next?.featureFlags === 'object' &&
        next?.featureFlags !== null
      ) {
        const _getEntries = (
          flagHash :MenuConfigItemFeatureFlagHash,
        ) :MenuConfigItemFeatureFlagsEntry[] | null => {
          if (!flagHash) return null;

          const entries = Object.entries(
            flagHash,
          ) as MenuConfigItemFeatureFlagsEntry[];

          return entries.length > 0 ? entries : null;
        };
        const _flagMatchesItsValue = ([
          _flag,
          _val,
        ] :MenuConfigItemFeatureFlagsEntry) :boolean => {
          if (_flag === 'any')
            // 'any' will be checked manually, so we pass it here
            return true;
          if (_val && typeof _val !== 'boolean') {
            // #sanitycheck
            console.warn(`MenuConfigService: Menu config 'featureFlags'
                property contains bad value for flag ${JSON.stringify(_flag)}:
                ${JSON.stringify(_val)}`);

            return false;
          }

          return this.ft.featureEnabled(_flag) === _val;
        };
        const _any = (flagHash :MenuConfigItemFeatureFlagHash) :boolean => {
          const entries = _getEntries(flagHash);

          if (!entries) return true; // no entries means no requirements, so we pass

          return entries.some(_flagMatchesItsValue);
        };
        const _all = (flagHash :MenuConfigItemFeatureFlagHash) :boolean => {
          const entries = _getEntries(flagHash);

          if (!entries) return true; // no entries means no requirements, so we pass

          return entries.every(_flagMatchesItsValue);
        };

        const flags = next.featureFlags as MenuConfigItemFeatureFlags;

        if (!_all(flags) || ('any' in flags && !_any(flags.any))) return acc; // exit without adding this menu item to the menu
      }

      if (
        next.permissions?.some((requiredPermission) => {
          // User Permission = {resource}.{action}.{target}
          // If the user has access to anything within the resource.view then they have access to this menu item.
          // If the route requires reports.read then reports.read.1 will work
          for (const [userPermission, value] of Object.entries(usersPermissions)) {
            const regex = new RegExp(requiredPermission + '.*');
            if (next.hideViewOnlyUsers && isViewOnlyUser) {
              return false;
            }
            if (userPermission.match(regex)) {
              return true;
            }
          }

          return false;
        }) ||
        next.submenu?.length
      ) {
        return [...acc, next];
      }

      return acc;
    }, []);
  }

  hasPermission(permission) {
    const userRole = this.authService.loginUser.role;

    return permission === '*' || permission.includes(userRole);
  }

  get userPermissions() :string[] {
    return Object.keys(this.permissionsService.getPermissions());
  }

  private userHasPermission(acceptedPermissions :string[]) :boolean {
    return acceptedPermissions.some((permission) =>
      this.userPermissions.includes(permission),
    );
  }

  loadConfigs(
    config :any,
    rPermissions :RPermission,
    displayPageSettings :any = {},
  ) {
    let items;
    items = this.checkPermissions(
      config.aside.items,
      rPermissions,
      displayPageSettings,
    );

    // overwrite aside menu items
    items = {
      ...config,
      aside: { ...config.aside, items },
    };

    this.menuConfig = items;
    this.onConfigUpdated$.next(this.menuConfig);
  }
}
